import { Dialog, findSpots, type Point, type Spot } from '@trippath/shared';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MapPanel } from '../map/MapPanel';
import { gpsOf } from './SpotField';
import { PAUSE } from './TownField';

interface Props {
  /** Where the map opens: the town, or the place picked before. */
  near: Point;
  /** The place picked before, pinned when the map opens. */
  picked?: Spot | null | undefined;
  /** What the place is called where none is picked: looked for at once, the first found pinned. */
  query?: string | undefined;
  onPick: (spot: Spot) => void;
  onClose: () => void;
}

/** The place a map pin shows, on its point. */
const PIN_ZOOM = 16;

/** Nothing drawn but the pin: one object, so a pin dropped does not redraw the map round it. */
const NOTHING = { points: [], lines: [] };

/**
 * A place picked on the map, as one drops a pin: pressed where it is, found by its name near the
 * town or by its GPS, named if one likes, and taken. Its name is its coordinates until it is
 * given one; the place picked before is pinned already.
 */
export function SpotMap({ near, picked = null, query = '', onPick, onClose }: Props) {
  const { t } = useTranslation();
  const ids = useId();
  const nameInput = useRef<HTMLInputElement>(null);
  const [point, setPoint] = useState<Point | null>(picked);
  const [name, setName] = useState(picked?.name ?? '');
  const [typed, setTyped] = useState(picked === null ? query : '');
  const [found, setFound] = useState<{ query: string; spots: Spot[] }>({
    query: '',
    spots: [],
  });
  const coordinates = point ? `${point.lat.toFixed(5)}, ${point.lon.toFixed(5)}` : '';
  const gps = gpsOf(typed);

  useEffect(() => {
    if (typed.trim().length < 3 || gpsOf(typed)) return;
    const stop = new AbortController();
    const timer = setTimeout(() => {
      findSpots(typed, near, stop.signal).then(
        (answer) => {
          setFound({ query: typed, spots: answer.spots });
          // Its name as said, nothing pinned yet: the first found is pinned for it.
          const [best] = answer.spots;
          if (best && typed === query) {
            setPoint({ lat: best.lat, lon: best.lon });
            setName(best.name);
          }
        },
        () => {
          setFound({ query: typed, spots: [] });
        },
      );
    }, PAUSE);
    return () => {
      clearTimeout(timer);
      stop.abort();
    };
  }, [typed, near, query]);

  const shown = gps ? [gps] : found.query === typed ? found.spots : [];
  const take = (spot: Spot) => {
    setPoint({ lat: spot.lat, lon: spot.lon });
    setName(spot.name);
    setTyped('');
  };

  return (
    <Dialog open onClose={onClose} title={t('spotMap.title')} wide>
      <div className="field spot-map-search">
        <label className="field-label" htmlFor={`${ids}-search`}>
          {t('spotMap.search')}
        </label>
        <input
          id={`${ids}-search`}
          className="text-input"
          type="text"
          autoComplete="off"
          value={typed}
          placeholder={t('spotMap.searchHint')}
          onChange={(event) => {
            setTyped(event.target.value);
          }}
        />
        {shown.length > 0 && (
          <ul className="towns" aria-label={t('spotMap.found')}>
            {shown.map((spot) => (
              <li key={`${spot.name}-${String(spot.lat)}-${String(spot.lon)}`}>
                <button
                  type="button"
                  className="option"
                  onClick={() => {
                    take(spot);
                  }}
                >
                  <span>
                    <span className="option-city">{spot.name}</span>
                    <span className="option-where">{spot.detail}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {gps === null && found.query === typed && typed !== '' && found.spots.length === 0 && (
          <p className="quiet town-note">{t('spotMap.none')}</p>
        )}
      </div>
      <div className="spot-map">
        <MapPanel
          data={NOTHING}
          label={t('spotMap.title')}
          fit={[point ?? near]}
          closest={PIN_ZOOM}
          cooperative={false}
          focus={point}
          onSpot={({ lat, lon }) => {
            setPoint({ lat, lon });
            nameInput.current?.focus();
          }}
        />
      </div>
      <div className="field">
        <label className="field-label" htmlFor={`${ids}-name`}>
          {t('spotMap.name')}
        </label>
        <input
          id={`${ids}-name`}
          ref={nameInput}
          className="text-input"
          type="text"
          autoComplete="off"
          value={name}
          placeholder={point ? coordinates : t('spotMap.nameHint')}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
      </div>
      <div className="dialog-actions">
        <button type="button" className="button" onClick={onClose}>
          {t('dialog.cancel')}
        </button>
        <button
          type="button"
          className="button button-primary"
          disabled={point === null}
          onClick={
            point
              ? () => {
                  onPick({
                    ...point,
                    name: name.trim() === '' ? coordinates : name.trim(),
                    detail: t('spotMap.detail'),
                  });
                }
              : undefined
          }
        >
          {t('spotMap.take')}
        </button>
      </div>
    </Dialog>
  );
}
